import { BrandingConfig, DEFAULT_BRANDING } from '@photobooth/types';

const BRANDING_STORAGE_KEY = 'photobooth_branding';
const BRANDING_ASSETS_KEY = 'photobooth_branding_assets';

interface BrandingAssetCache {
  logoBlob?: Blob;
  logoSecondaryBlob?: Blob;
  watermarkBlob?: Blob;
  backgroundBlob?: Blob;
  syncedAt: number;
}

/**
 * Load branding configuration from localStorage
 * Falls back to default branding if not found
 */
export async function loadBranding(): Promise<BrandingConfig> {
  try {
    const stored = localStorage.getItem(BRANDING_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Merge with defaults to ensure all fields exist
      return { ...DEFAULT_BRANDING, ...parsed };
    }
  } catch (error) {
    console.error('Failed to load branding from localStorage:', error);
  }
  return DEFAULT_BRANDING;
}

/**
 * Save branding configuration to localStorage
 */
export async function saveBranding(branding: BrandingConfig): Promise<void> {
  try {
    localStorage.setItem(BRANDING_STORAGE_KEY, JSON.stringify(branding));
  } catch (error) {
    console.error('Failed to save branding to localStorage:', error);
    throw error;
  }
}

/**
 * Clear branding configuration (reset to default)
 */
export async function clearBranding(): Promise<void> {
  try {
    localStorage.removeItem(BRANDING_STORAGE_KEY);
    localStorage.removeItem(BRANDING_ASSETS_KEY);
  } catch (error) {
    console.error('Failed to clear branding:', error);
  }
}

/**
 * Download and cache branding assets (logos, watermarks, etc.)
 * This is called during sync to ensure offline availability
 */
export async function cacheBrandingAssets(branding: BrandingConfig): Promise<void> {
  const assets: BrandingAssetCache = {
    syncedAt: Date.now(),
  };

  try {
    // Cache primary logo
    if (branding.logos.primary) {
      assets.logoBlob = await fetchAsBlob(branding.logos.primary);
    }

    // Cache secondary logo
    if (branding.logos.secondary) {
      assets.logoSecondaryBlob = await fetchAsBlob(branding.logos.secondary);
    }

    // Cache watermark
    if (branding.logos.watermark) {
      assets.watermarkBlob = await fetchAsBlob(branding.logos.watermark);
    }

    // Cache background image
    if (branding.assets.backgroundImage) {
      assets.backgroundBlob = await fetchAsBlob(branding.assets.backgroundImage);
    }

    // Store in localStorage (as base64 for simplicity)
    // For larger assets, consider using IndexedDB instead
    localStorage.setItem(BRANDING_ASSETS_KEY, JSON.stringify({
      ...assets,
      logoBlob: assets.logoBlob ? await blobToBase64(assets.logoBlob) : undefined,
      logoSecondaryBlob: assets.logoSecondaryBlob ? await blobToBase64(assets.logoSecondaryBlob) : undefined,
      watermarkBlob: assets.watermarkBlob ? await blobToBase64(assets.watermarkBlob) : undefined,
      backgroundBlob: assets.backgroundBlob ? await blobToBase64(assets.backgroundBlob) : undefined,
    }));
  } catch (error) {
    console.error('Failed to cache branding assets:', error);
  }
}

/**
 * Get cached branding assets
 */
export async function getCachedAssets(): Promise<BrandingAssetCache | null> {
  try {
    const stored = localStorage.getItem(BRANDING_ASSETS_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Convert base64 back to Blobs
      return {
        logoBlob: parsed.logoBlob ? await base64ToBlob(parsed.logoBlob) : undefined,
        logoSecondaryBlob: parsed.logoSecondaryBlob ? await base64ToBlob(parsed.logoSecondaryBlob) : undefined,
        watermarkBlob: parsed.watermarkBlob ? await base64ToBlob(parsed.watermarkBlob) : undefined,
        backgroundBlob: parsed.backgroundBlob ? await base64ToBlob(parsed.backgroundBlob) : undefined,
        syncedAt: parsed.syncedAt,
      };
    }
  } catch (error) {
    console.error('Failed to get cached assets:', error);
  }
  return null;
}

/**
 * Helper: Fetch URL as Blob
 */
async function fetchAsBlob(url: string): Promise<Blob> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch ${url}: ${response.statusText}`);
  }
  return response.blob();
}

/**
 * Helper: Convert Blob to base64
 */
function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Helper: Convert base64 to Blob
 */
async function base64ToBlob(base64: string): Promise<Blob> {
  const response = await fetch(base64);
  return response.blob();
}
